import { toRfc3339Bound } from './api'
import { ALERT_CHANNELS } from './types'
import type { AlertChannel, AlertFilters, AlertStatus, Priority, SortKey } from './types'
import type { UrlFilterCodec } from '@/lib/useUrlFilters'

/**
 * URL <-> state codec for the alerts queue. Every dimension an operator can
 * set is addressable, so dashboard tiles, shared links, refresh and
 * back/forward all reproduce the same view:
 *
 *   status (repeat) | priority (repeat) | channel (repeat) | scenario (repeat)
 *   queue (repeat)  | assigned_to (repeat or `unassigned`) | q
 *   sla_breach=true | sla_at_risk=true | amount_min_minor | amount_max_minor
 *   from / to (RFC3339 or YYYY-MM-DD) | sort=field:dir (repeat) | saved
 *
 * URL contents are untrusted input: every value is validated against the
 * closed vocabulary (or length-bounded for free-form ids) and unknown
 * values are dropped.
 */

/** Statuses shown when the URL carries no `status` param: everything still actionable. */
export const DEFAULT_STATUS: AlertStatus[] = ['open', 'in_review', 'escalated']

/** Every status selectable in the Status chip — active AND closed, so closed alerts are findable (RT-09). */
export const SELECTABLE_STATUSES: readonly AlertStatus[] = [
  'open',
  'in_review',
  'escalated',
  'merged',
  'closed_fraud',
  'closed_false_positive',
  'closed_duplicate',
  'closed_other',
]

export const DEFAULT_SORT: SortKey[] = [{ field: 'created_at', dir: 'desc' }]

/** Sentinel written to `status` when the operator explicitly wants NO status filter. */
const STATUS_ANY = 'any'

const PRIORITIES: readonly Priority[] = ['critical', 'high', 'medium', 'low']
const SORT_FIELDS: readonly SortKey['field'][] = [
  'priority',
  'alert_number',
  'scenario_name',
  'score',
  'amount_minor',
  'channel',
  'status',
  'sla_remaining',
  'created_at',
]

const MAX_ID_LEN = 128
const MAX_VALUES = 50
/** Server caps `q` at 64 chars (alerts list handler); input + codec mirror it. */
export const MAX_Q_LEN = 64
const SAVED_RE = /^[A-Za-z0-9_-]{1,64}$/

export interface QueueUrlState {
  filters: AlertFilters
  sort: SortKey[]
  /** Saved-filter id (or the `mine` alias) the view was opened from. */
  saved: string | null
}

function pick<T extends string>(values: string[], allowed: readonly T[]): T[] {
  const out: T[] = []
  for (const v of values) {
    const hit = allowed.find((a) => a === v)
    if (hit !== undefined && !out.includes(hit)) out.push(hit)
  }
  return out
}

function ids(values: string[]): string[] {
  const out: string[] = []
  for (const raw of values) {
    const v = raw.trim()
    if (v !== '' && v.length <= MAX_ID_LEN && !out.includes(v)) out.push(v)
    if (out.length >= MAX_VALUES) break
  }
  return out
}

function nonNegInt(raw: string | null): number | undefined {
  if (raw === null || !/^\d{1,15}$/.test(raw)) return undefined
  return Number(raw)
}

function sameSet(a: readonly string[], b: readonly string[]): boolean {
  return a.length === b.length && a.every((v) => b.includes(v))
}

function sameSort(a: readonly SortKey[], b: readonly SortKey[]): boolean {
  return a.length === b.length && a.every((s, i) => s.field === b[i]?.field && s.dir === b[i]?.dir)
}

export function parseQueueUrl(sp: URLSearchParams): QueueUrlState {
  const filters: AlertFilters = {}

  const rawStatus = sp.getAll('status')
  if (rawStatus.includes(STATUS_ANY)) {
    // explicit "all statuses": leave undefined
  } else {
    const picked = pick(rawStatus, SELECTABLE_STATUSES)
    filters.status = picked.length > 0 ? picked : [...DEFAULT_STATUS]
  }

  const priority = pick(sp.getAll('priority'), PRIORITIES)
  if (priority.length > 0) filters.priority = priority
  const channel: AlertChannel[] = pick(sp.getAll('channel'), ALERT_CHANNELS)
  if (channel.length > 0) filters.channel = channel
  const scenario = ids(sp.getAll('scenario'))
  if (scenario.length > 0) filters.scenario = scenario
  const queue = ids(sp.getAll('queue'))
  if (queue.length > 0) filters.queue_id = queue

  const assigned = ids(sp.getAll('assigned_to'))
  if (assigned.includes('unassigned')) filters.assigned_to = 'unassigned'
  else if (assigned.length > 0) filters.assigned_to = assigned

  if (sp.get('sla_breach') === 'true') filters.sla_breached = true
  if (sp.get('sla_at_risk') === 'true') filters.sla_at_risk = true
  const min = nonNegInt(sp.get('amount_min_minor'))
  if (min !== undefined) filters.amount_min_minor = min
  const max = nonNegInt(sp.get('amount_max_minor'))
  if (max !== undefined) filters.amount_max_minor = max

  const from = sp.get('from')
  if (from && toRfc3339Bound(from, 'start')) filters.date_from = from
  const to = sp.get('to')
  if (to && toRfc3339Bound(to, 'end')) filters.date_to = to

  const q = sp.get('q')?.trim().slice(0, MAX_Q_LEN)
  if (q) filters.q = q

  const sort: SortKey[] = []
  for (const spec of sp.getAll('sort')) {
    const [field, dir] = spec.split(':')
    const f = SORT_FIELDS.find((s) => s === field)
    if (f !== undefined && (dir === 'asc' || dir === 'desc') && !sort.some((s) => s.field === f)) {
      sort.push({ field: f, dir })
    }
  }

  const savedRaw = sp.get('saved')
  return {
    filters,
    sort: sort.length > 0 ? sort : DEFAULT_SORT.map((s) => ({ ...s })),
    saved: savedRaw !== null && SAVED_RE.test(savedRaw) ? savedRaw : null,
  }
}

const OWNED_KEYS = [
  'status',
  'priority',
  'channel',
  'scenario',
  'queue',
  'assigned_to',
  'q',
  'sla_breach',
  'sla_at_risk',
  'amount_min_minor',
  'amount_max_minor',
  'from',
  'to',
  'sort',
  'saved',
] as const

export function writeQueueUrl(state: QueueUrlState, sp: URLSearchParams): void {
  for (const k of OWNED_KEYS) sp.delete(k)
  const f = state.filters

  if (!f.status || f.status.length === 0) sp.append('status', STATUS_ANY)
  else if (!sameSet(f.status, DEFAULT_STATUS)) for (const s of f.status) sp.append('status', s)

  for (const v of f.priority ?? []) sp.append('priority', v)
  for (const v of f.channel ?? []) sp.append('channel', v)
  for (const v of f.scenario ?? []) sp.append('scenario', v)
  for (const v of f.queue_id ?? []) sp.append('queue', v)
  if (f.assigned_to === 'unassigned') sp.append('assigned_to', 'unassigned')
  else for (const v of f.assigned_to ?? []) sp.append('assigned_to', v)
  if (f.q) sp.set('q', f.q)
  if (f.sla_breached) sp.set('sla_breach', 'true')
  if (f.sla_at_risk) sp.set('sla_at_risk', 'true')
  if (f.amount_min_minor !== undefined) sp.set('amount_min_minor', String(f.amount_min_minor))
  if (f.amount_max_minor !== undefined) sp.set('amount_max_minor', String(f.amount_max_minor))
  if (f.date_from) sp.set('from', f.date_from)
  if (f.date_to) sp.set('to', f.date_to)
  if (!sameSort(state.sort, DEFAULT_SORT)) {
    for (const s of state.sort) sp.append('sort', `${String(s.field)}:${s.dir}`)
  }
  if (state.saved) sp.set('saved', state.saved)
}

export const queueUrlCodec: UrlFilterCodec<QueueUrlState> = {
  parse: parseQueueUrl,
  write: writeQueueUrl,
}
